/**
 * SEO-Optimised Global Footer Configuration
 * 
 * Architectural Principles:
 * - Footer ≠ Navigation (does not duplicate header mega menu)
 * - Canonical-only linking (no redirects, no query params, no hash fragments)
 * - One topic → One primary URL (avoids keyword cannibalisation)
 * - Max 28-36 internal links (excluding legal/social)
 * 
 * This footer acts as:
 * - Canonical authority signal
 * - Low-noise internal linking backbone
 * - Local + entity reinforcement layer
 */

import { COSMETIC_COLLEGE_APP_PAGE_PATH } from "@/utils/student-app";
import { TRAINING_HUB_PATH, TRAINING_PERSONAS } from "@/config/training-personas";

export type FooterLinkGroup =
  | "pillars"
  | "priority"
  | "locations"
  | "resources"
  | "company"
  | "legal";

export type SEOIntent = "primary" | "guide" | "location";

export type FooterLink = {
  label: string;
  href: string;
  group: FooterLinkGroup;
  seoTopic?: string;
  seoIntent?: SEOIntent;
  rel?: string;
  target?: "_blank";
};

export type FooterColumn = {
  title: string;
  group: FooterLinkGroup;
  links: FooterLink[];
};

export type FooterConfig = {
  columns: FooterColumn[];
  maxInternalLinks: number;
};

/**
 * Column 2: Core Course Pillars (Definition Layer)
 * Top-level canonical hubs only - defines what the site is about
 */
const pillarsColumn: FooterColumn = {
  title: "Training",
  group: "pillars",
  links: [
    {
      label: "VTCT Qualifications",
      href: "/courses/vtct/",
      group: "pillars",
      seoTopic: "vtct-qualifications",
      seoIntent: "primary",
    },
    {
      label: "CPD Short Courses",
      href: "/courses/cpd/",
      group: "pillars",
      seoTopic: "cpd-courses",
      seoIntent: "primary",
    },
    {
      label: "Build Your CPD Package",
      href: "/build-your-cpd-package/",
      group: "pillars",
      seoTopic: "cpd-package-builder",
      seoIntent: "primary",
    },
    {
      label: "Online Courses",
      href: "/courses/elearning/",
      group: "pillars",
      seoTopic: "online-training",
      seoIntent: "primary",
    },
    {
      label: "Course Prices",
      href: "/course-prices/",
      group: "pillars",
      seoTopic: "course-prices",
      seoIntent: "guide",
    },
    {
      label: "Aesthetics Training",
      href: "/courses/aesthetics/",
      group: "pillars",
      seoTopic: "aesthetics-training",
      seoIntent: "primary",
    },
    {
      label: "Beauty Training",
      href: "/courses/beauty/",
      group: "pillars",
      seoTopic: "beauty-training",
      seoIntent: "primary",
    },
    {
      label: "Permanent Makeup Training",
      href: "/courses/semi-permanent-makeup/",
      group: "pillars",
      seoTopic: "permanent-makeup-training",
      seoIntent: "primary",
    },
    {
      label: "Hairdressing Training",
      href: "/courses/hair/",
      group: "pillars",
      seoTopic: "hairdressing-training",
      seoIntent: "primary",
    },
    {
      label: "Education & Assessing",
      href: "/courses/education/",
      group: "pillars",
      seoTopic: "education-training",
      seoIntent: "primary",
    },
    {
      label: "Mentorship",
      href: "/mentorship/",
      group: "pillars",
      seoTopic: "mentorship",
      seoIntent: "guide",
    },
    {
      label: "Clinic Team & Treatment Planner",
      href: "/resources/clinic-team-and-treatment-pathway-planner/",
      group: "pillars",
      seoTopic: "clinic-team-treatment-planner",
      seoIntent: "guide",
    },
  ],
};

/**
 * Column 3: Priority Training Courses (Ranking Levers)
 * 8-10 links max - each must be the single primary URL for that topic
 * No VTCT + CPD + Online duplicates
 */
const priorityColumn: FooterColumn = {
  title: "Popular Courses",
  group: "priority",
  links: [
    {
      label: "VTCT Laser Hair Removal Course",
      href: "/courses/vtct/laser-hair-removal/",
      group: "priority",
      seoTopic: "laser-hair-removal",
      seoIntent: "primary",
    },
    {
      label: "VTCT Level 3 Access to Aesthetics",
      href: "/courses/vtct/level-3-certificate-in-access-to-aesthetic-therapies/",
      group: "priority",
      seoTopic: "level-3-aesthetics",
      seoIntent: "primary",
    },
    {
      label: "VTCT Level 7 Injectables Diploma",
      href: "/courses/vtct/level-7-diploma-in-non-surgical-aesthetic-injectable-procedures/",
      group: "priority",
      seoTopic: "level-7-injectables",
      seoIntent: "primary",
    },
    {
      label: "VTCT Laser Tattoo Removal Course",
      href: "/courses/vtct/level-5-certificate-in-laser-tattoo-removal/",
      group: "priority",
      seoTopic: "laser-tattoo-removal",
      seoIntent: "primary",
    },
    {
      label: "Microneedling Course",
      href: "/courses/vtct/level-4-in-skin-microneedling/",
      group: "priority",
      seoTopic: "microneedling-training",
      seoIntent: "primary",
    },
    {
      label: "Lymphatic Drainage Massage Training",
      href: "/courses/beauty/lymphatic-drainage-massage/",
      group: "priority",
      seoTopic: "lymphatic-drainage",
      seoIntent: "primary",
    },
    {
      label: "Polynucleotides Training Course",
      href: "/courses/aesthetics/polynucleotides-skin-rejuvenation/",
      group: "priority",
      seoTopic: "polynucleotides",
      seoIntent: "primary",
    },
    {
      label: "VTCT Level 4 Advanced Aesthetic Therapies for Skin Rejuvenation",
      href: "/courses/vtct/level-4-certificate-in-advanced-aesthetic-therapies-for-skin/",
      group: "priority",
      seoTopic: "level-4-aesthetics",
      seoIntent: "primary",
    },
  ],
};

/**
 * Column 4: Career & Authority Hubs (Support Layer)
 * Builds topical authority, funnels users to courses, supports E-E-A-T
 */
const resourcesColumn: FooterColumn = {
  title: "Choose Your Training",
  group: "resources",
  links: [
    {
      label: "Find your training route",
      href: TRAINING_HUB_PATH,
      group: "resources",
      seoIntent: "guide",
    },
    ...TRAINING_PERSONAS.map((persona) => ({
      label: persona.label,
      href: persona.href,
      group: "resources" as const,
      seoIntent: "guide" as const,
    })),
  ],
};

/**
 * Column 5: Locations, Company & Legal
 */
const companyColumn: FooterColumn = {
  title: "Company",
  group: "company",
  links: [
    {
      label: "Aesthetics machines & equipment - Ventra Aesthetics",
      href: "https://www.ventra-aesthetics.com/products",
      group: "company",
    },
    {
      label: "About Us",
      href: "/about/",
      group: "company",
    },
    {
      label: "Reviews",
      href: "/reviews/",
      group: "company",
      seoTopic: "student-reviews",
      seoIntent: "guide",
    },
    {
      label: "Meet The Team",
      href: "/team/",
      group: "company",
    },
    {
      label: "Blog",
      href: "/blog/",
      group: "company",
    },
    {
      label: "Training Calendar",
      href: "/training-course-calendar/",
      group: "company",
    },
    {
      label: "Become a Model",
      href: "/models/",
      group: "company",
    },
    {
      label: "Model Club Unlimited",
      href: "/models/membership/",
      group: "company",
    },
    {
      label: "Finance Options",
      href: "/finance/",
      group: "company",
    },
    {
      label: "Cosmetic Careers",
      href: "https://cosmetic.careers",
      group: "company",
      target: "_blank",
    },
    {
      label: "Managed Forms",
      href: "https://www.withmanaged.com/digital-forms",
      group: "company",
      target: "_blank",
    },
    {
      label: "Cosmetic College App",
      href: COSMETIC_COLLEGE_APP_PAGE_PATH,
      group: "company",
    },
    {
      label: "With Managed",
      href: "https://www.withmanaged.com/r/cosmetic-college",
      group: "company",
      target: "_blank",
    },
  ],
};

const locationsColumn: FooterColumn = {
  title: "Training Locations",
  group: "locations",
  links: [
    {
      label: "Our Training Centre",
      href: "/location/",
      group: "locations",
      seoTopic: "orpington-training-centre",
      seoIntent: "location",
    },
    {
      label: "Aesthetics Training London",
      href: "/aesthetics-training-london/",
      group: "locations",
      seoTopic: "london-training",
      seoIntent: "location",
    },
    {
      label: "Aesthetics Training Kent",
      href: "/aesthetics-training-kent/",
      group: "locations",
      seoTopic: "kent-training",
      seoIntent: "location",
    },
    {
      label: "Aesthetic Training in the South East",
      href: "/aesthetics-training-london/",
      group: "locations",
      seoTopic: "south-east-training",
      seoIntent: "location",
    },
    {
      label: "UK-Wide Aesthetic Training",
      href: "/courses/aesthetics/",
      group: "locations",
      seoTopic: "uk-training",
      seoIntent: "location",
    },
  ],
};

const legalColumn: FooterColumn = {
  title: "Legal",
  group: "legal",
  links: [
    {
      label: "Privacy Policy",
      href: "/privacy-policy/",
      group: "legal",
    },
    {
      label: "Terms and Conditions",
      href: "/terms-and-conditions/",
      group: "legal",
    },
  ],
};

/**
 * Footer Configuration
 * 
 * Build-time validation ensures:
 * - Max 28-36 internal links (excluding legal/social)
 * - All hrefs are canonical (no redirects, no query params, no hash fragments)
 * - One topic → One primary URL (no duplicates)
 */
export const footerConfig: FooterConfig = {
  columns: [
    pillarsColumn,
    priorityColumn,
    resourcesColumn,
    companyColumn,
    locationsColumn,
    legalColumn,
  ],
  maxInternalLinks: 42,
};

/**
 * Build-time validation: Count internal links (excluding legal and external URLs)
 */
export function validateFooterConfig(): {
  isValid: boolean;
  internalLinkCount: number;
  errors: string[];
} {
  const errors: string[] = [];
  let internalLinkCount = 0;

  footerConfig.columns.forEach((column) => {
    column.links.forEach((link) => {
      const isExternal = link.href.startsWith("http");
      const isLegal = column.group === "legal";

      if (!isLegal && !isExternal) {
        internalLinkCount += 1;
      }

      // Validate hrefs are canonical for internal links
      if (!isExternal && link.href.includes("?")) {
        errors.push(`Link "${link.label}" contains query parameters: ${link.href}`);
      }
      if (!isExternal && link.href.includes("#")) {
        errors.push(`Link "${link.label}" contains hash fragment: ${link.href}`);
      }
      if (!link.href.startsWith("/") && !link.href.startsWith("http")) {
        errors.push(`Link "${link.label}" has invalid href format: ${link.href}`);
      }
    });
  });

  if (internalLinkCount > footerConfig.maxInternalLinks) {
    errors.push(
      `Internal link count (${internalLinkCount}) exceeds maximum (${footerConfig.maxInternalLinks})`
    );
  }

  return {
    isValid: errors.length === 0,
    internalLinkCount,
    errors,
  };
}

// Validate at module load (build-time)
if (typeof window === "undefined") {
  const validation = validateFooterConfig();
  if (!validation.isValid) {
    console.error("Footer config validation failed:", validation.errors);
    if (process.env.NODE_ENV === "production") {
      throw new Error(
        `Footer config validation failed: ${validation.errors.join(", ")}`
      );
    }
  }
}
